import React, { useCallback } from 'react';
import { generatedTypesNew, Maybe, PaymentMethod } from '@css-xp/graphtypes';
import { SummaryGridItem, SummarySection, FormattedAddress, TaxExempt, useFeatureToggle } from '@css-xp/base';
import { capitalize } from 'lodash';
import { SubscriptionTableForShipToParty } from '../../../tabs/invoice-group-tabs/src/subscription-table-for-ship-to-party';
import { InvoicesTableForShipToParty } from '../../../tabs/invoice-group-tabs/src/invoices-table-for-ship-to-party';
import { SubscriptionTableForPaymentMethod } from '../../../tabs/invoice-group-tabs/src/subscription-table-for-payment-method';
import { InvoicesTableForPaymentMethod } from '../../../tabs/invoice-group-tabs/src/invoices-table-for-payment-method';
import { getPaymentMethodOption } from '@css-xp/base/payment-methods-table-v2/src/payment-methods-table-txa-v2';
import { withApollo } from '@css-xp/base/graphql-provider';
import './view-linked-subscriptions-sidebar.scss';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
interface Props {
  transactionAccountId: string;
  shipToAddress?: generatedTypesNew.CommerceAddress;
  paymentMethod?: generatedTypesNew.PaymentMethod;
  defaultPaymentMethodId?: string | undefined | null;
  availableInvoiceGroups?: generatedTypesNew.Maybe<generatedTypesNew.InvoiceGroup[]>;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
}

export const ViewLinkedSubscriptionsSidebar = withApollo(
  ({
    shipToAddress,
    transactionAccountId,
    paymentMethod,
    defaultPaymentMethodId,
    availableInvoiceGroups,
    availablePaymentMethods,
  }: Props) => {
    const [isEnabled] = useFeatureToggle();
    const isEnableCreditManagement = isEnabled(ENABLE_CREDIT_MANAGEMENT);
    const renderPaymentMethodDetails = useCallback(() => {
      if (paymentMethod?.id) {
        return getPaymentMethodOption({ paymentMethod, index: 1, defaultPaymentMethodId, isEnableCreditManagement });
      }
    }, [paymentMethod, defaultPaymentMethodId, isEnableCreditManagement]);

    return (
      <div className="view-linked-subscriptions-sidebar">
        <h2 className="title">Linked subscriptions & invoices</h2>
        {shipToAddress?.id && (
          <>
            <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr">
              <SummaryGridItem className="section-item-internal address-details">
                <div className="sold-to-address">
                  <label>Sold-to address</label>
                  {shipToAddress?.addressLine1 ? (
                    <div>
                      <FormattedAddress
                        addressLine1={shipToAddress?.addressLine1}
                        addressLine2={shipToAddress?.addressLine2}
                        city={shipToAddress?.city}
                        country={shipToAddress?.country}
                        postcode={shipToAddress?.postcode}
                        state={shipToAddress?.state}
                        organizationName={shipToAddress?.name}
                      />
                    </div>
                  ) : (
                    <p>No address information</p>
                  )}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <div>
                  <label>Pricing eligibility</label>
                  {shipToAddress?.shipToPriceEligibility ? (
                    <div>{capitalize(shipToAddress.shipToPriceEligibility)}</div>
                  ) : (
                    <p>No pricing eligibility found</p>
                  )}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <TaxExempt taxExempt={shipToAddress?.taxExempt ? 'Yes' : 'No'} />
              </SummaryGridItem>
            </SummarySection>
            <SubscriptionTableForShipToParty shipToPartyId={shipToAddress?.id} label="Linked subscriptions" />
            <InvoicesTableForShipToParty
              shipToPartyId={shipToAddress?.id}
              transactionAccountId={transactionAccountId}
              label="Linked invoices"
              availablePaymentMethods={availablePaymentMethods}
            />
          </>
        )}
        {paymentMethod?.id && (
          <>
            <div className="payment-method-details">
              <h4>Payment method</h4>
              {renderPaymentMethodDetails()}
            </div>
            <SubscriptionTableForPaymentMethod
              paymentMethodId={paymentMethod?.id}
              label="Linked subscriptions"
              availableInvoiceGroups={availableInvoiceGroups}
            />
            <InvoicesTableForPaymentMethod
              paymentMethodId={paymentMethod?.id}
              transactionAccountId={transactionAccountId}
              label="Linked invoices"
            />
          </>
        )}
      </div>
    );
  }
);
